/**
 * Environment Configuration for Smart Storage Checker
 * Centralizes all environment-related settings
 */

// Environment detection
export const NODE_ENV = process.env.NODE_ENV || 'development';
export const IS_DEVELOPMENT = NODE_ENV === 'development';
export const IS_PRODUCTION = NODE_ENV === 'production';
export const IS_TEST = NODE_ENV === 'test';

// API Configuration
export const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  retries: 2,
  retryDelay: 1000,
};

// Application Environment
export const APP_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';

// Feature Flags
export const FEATURES = {
  debug: process.env.REACT_APP_ENABLE_DEBUG === 'true' || IS_DEVELOPMENT,
  analytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true' && IS_PRODUCTION,
  errorReporting: process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true' && IS_PRODUCTION,
  devTools: process.env.REACT_APP_DEV_TOOLS === 'true' || IS_DEVELOPMENT,
};

// File Upload Configuration
export const UPLOAD_CONFIG = {
  maxFileSize: parseInt(process.env.REACT_APP_MAX_FILE_SIZE) || 50 * 1024 * 1024, // 50MB
  allowedTypes: process.env.REACT_APP_ALLOWED_FILE_TYPES?.split(',') || [
    'text/csv',
    'application/vnd.ms-excel',
    'application/csv',
  ],
  acceptedExtensions: ['.csv'],
  apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
};

// Cache Configuration
export const CACHE_CONFIG = {
  enabled: process.env.REACT_APP_CACHE_ENABLED !== 'false',
  defaultTTL: parseInt(process.env.REACT_APP_CACHE_TTL) || 5 * 60 * 1000, // 5 minutes
  maxEntries: 100,
  storageKey: 'smart_storage_cache',
};

// UI Configuration
export const UI_CONFIG = {
  theme: {
    mode: process.env.REACT_APP_THEME_MODE || 'light',
    primary: '#1f77b4',
    secondary: '#ff7f0e',
  },
  language: process.env.REACT_APP_LANGUAGE || 'de',
  notifications: {
    position: 'top-right',
    autoHideDuration: 5000,
  },
};

// Analytics Configuration
export const ANALYTICS_CONFIG = {
  googleAnalyticsId: process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
  hotjarId: process.env.REACT_APP_HOTJAR_ID,
  enabled: FEATURES.analytics && IS_PRODUCTION,
};

// Error Tracking Configuration
export const ERROR_TRACKING_CONFIG = {
  sentryDsn: process.env.REACT_APP_SENTRY_DSN,
  sentryEnvironment: process.env.REACT_APP_SENTRY_ENVIRONMENT || APP_ENVIRONMENT,
  enabled: FEATURES.errorReporting && IS_PRODUCTION,
  sampleRate: IS_PRODUCTION ? 0.1 : 1.0,
};

// Development Configuration
export const DEV_CONFIG = {
  enableMockApi: process.env.REACT_APP_ENABLE_MOCK_API === 'true',
  mockDelay: parseInt(process.env.REACT_APP_MOCK_DELAY) || 1000,
  enableHotReload: IS_DEVELOPMENT,
  showDebugInfo: FEATURES.debug,
};

// Validation Configuration
export const VALIDATION_CONFIG = {
  csv: {
    requiredColumns: ['timestamp', 'current_A', 'voltage_V', 'power_W'],
    minRows: 100,
    maxRows: 1000000,
    dateFormats: [
      'YYYY-MM-DD HH:mm:ss',
      'DD.MM.YYYY HH:mm:ss',
      'MM/DD/YYYY HH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss',
    ],
  },
  analysis: {
    minDataPoints: 24 * 60, // 24 hours of minute data
    maxGapMinutes: 60,
  },
};

// Performance Configuration
export const PERFORMANCE_CONFIG = {
  lazyLoading: IS_PRODUCTION,
  bundleSplitting: IS_PRODUCTION,
  imageOptimization: IS_PRODUCTION,
  cacheStrategyByRoute: {
    '/': 'cache-first',
    '/upload': 'network-first',
    '/results': 'cache-first',
  },
};

// Security Configuration
export const SECURITY_CONFIG = {
  contentSecurityPolicy: IS_PRODUCTION,
  httpsOnly: IS_PRODUCTION,
  corsOrigins: IS_DEVELOPMENT
    ? ['http://localhost:3000', 'http://localhost:8000']
    : [process.env.REACT_APP_CORS_ORIGIN],
};

// Logging Configuration
export const LOGGING_CONFIG = {
  level: IS_DEVELOPMENT ? 'debug' : 'warn',
  enableConsole: !IS_PRODUCTION,
  enableRemote: IS_PRODUCTION && FEATURES.errorReporting,
  categories: {
    api: FEATURES.debug,
    navigation: FEATURES.debug,
    user: true,
    error: true,
  },
};

// Export validation function
export function validateEnvironment() {
  const errors = [];
  const warnings = [];

  // Required variables
  if (!API_CONFIG.baseURL) {
    errors.push('REACT_APP_API_URL is required');
  }

  // Production-specific validations
  if (IS_PRODUCTION) {
    if (FEATURES.analytics && !ANALYTICS_CONFIG.googleAnalyticsId) {
      warnings.push('Analytics enabled but REACT_APP_GOOGLE_ANALYTICS_ID not set');
    }

    if (FEATURES.errorReporting && !ERROR_TRACKING_CONFIG.sentryDsn) {
      warnings.push('Error reporting enabled but REACT_APP_SENTRY_DSN not set');
    }
  }

  // Log validation results
  if (FEATURES.debug) {
    console.group('Environment Validation');
    console.log('Environment:', APP_ENVIRONMENT);
    console.log('API URL:', API_CONFIG.baseURL);
    console.log('Features:', FEATURES);

    if (warnings.length > 0) {
      console.warn('Warnings:', warnings);
    }

    if (errors.length > 0) {
      console.error('Errors:', errors);
    }

    console.groupEnd();
  }

  return { errors, warnings };
}

// Auto-validate on import in development
if (IS_DEVELOPMENT) {
  validateEnvironment();
}

// Default export with all configs
export default {
  NODE_ENV,
  IS_DEVELOPMENT,
  IS_PRODUCTION,
  IS_TEST,
  APP_ENVIRONMENT,
  API_CONFIG,
  FEATURES,
  UPLOAD_CONFIG,
  CACHE_CONFIG,
  UI_CONFIG,
  ANALYTICS_CONFIG,
  ERROR_TRACKING_CONFIG,
  DEV_CONFIG,
  VALIDATION_CONFIG,
  PERFORMANCE_CONFIG,
  SECURITY_CONFIG,
  LOGGING_CONFIG,
  validateEnvironment,
};